/**
 * react-router-dom compatibility shim backed by TanStack Router.
 * Aliased in vite.config so legacy `from 'react-router-dom'` imports resolve here.
 */
import * as React from "react";
import {
  Outlet as TanstackOutlet,
  useRouter,
  useRouterState,
  useParams as useTanstackParams,
} from "@tanstack/react-router";

export type RouteObject = Record<string, unknown>;

function splitHash(to: string): { path: string; hash?: string } {
  const i = to.indexOf("#");
  if (i === -1) return { path: to };
  return { path: to.slice(0, i) || "/", hash: to.slice(i + 1) };
}

function splitTo(to: string): { path: string; search?: Record<string, string>; hash?: string } {
  const { path: withoutHash, hash } = splitHash(to);
  const i = withoutHash.indexOf("?");
  if (i === -1) return { path: withoutHash, hash };
  const path = withoutHash.slice(0, i) || "/";
  const params = new URLSearchParams(withoutHash.slice(i + 1));
  return { path, search: Object.fromEntries(params.entries()), hash };
}

function isExternal(to: string): boolean {
  return /^(https?:)?\/\//i.test(to) || /^(mailto:|tel:|#)/i.test(to);
}

export function useLocation() {
  return useRouterState({
    select: (s) => {
      const loc = s.location;
      return {
        pathname: loc.pathname,
        search: loc.searchStr ?? "",
        hash: loc.hash ? `#${loc.hash}` : "",
        state: loc.state,
        key: loc.pathname + (loc.searchStr ?? ""),
      };
    },
  });
}

export function useNavigate() {
  const router = useRouter();
  return React.useCallback(
    (to: string | number, options?: { replace?: boolean; state?: unknown }) => {
      if (typeof to === "number") {
        if (typeof window !== "undefined") window.history.go(to);
        return;
      }
      if (isExternal(to)) {
        if (typeof window !== "undefined") window.location.href = to;
        return;
      }
      const { path, search, hash } = splitTo(to);
      router.navigate({ to: path, search, hash, replace: options?.replace } as never);
    },
    [router],
  );
}

export function useParams<T = Record<string, string | undefined>>(): T {
  return useTanstackParams({ strict: false } as never) as T;
}

export function useSearchParams(): [
  URLSearchParams,
  (next: URLSearchParams | Record<string, string> | ((p: URLSearchParams) => URLSearchParams)) => void,
] {
  const location = useLocation();
  const router = useRouter();
  const params = new URLSearchParams(location.search);
  const setSearchParams = (
    next:
      | URLSearchParams
      | Record<string, string>
      | ((p: URLSearchParams) => URLSearchParams),
  ) => {
    let resolved: URLSearchParams;
    if (typeof next === "function") {
      resolved = next(new URLSearchParams(location.search));
    } else if (next instanceof URLSearchParams) {
      resolved = next;
    } else {
      resolved = new URLSearchParams(next);
    }
    router.navigate({
      to: location.pathname,
      search: Object.fromEntries(resolved.entries()),
    } as never);
  };
  return [params, setSearchParams as never];
}

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  replace?: boolean;
  state?: unknown;
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, onClick, target, children, ...rest },
  ref,
) {
  const navigate = useNavigate();
  const external = isExternal(to);

  // Rely on the browser's native click event only. iOS Safari fires `click`
  // reliably on anchor taps; reimplementing touch handling caused ghost
  // clicks, double-taps, and dropped onClick side effects (menus/panels not
  // closing, navigation getting swallowed).
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (external || target === "_blank") return;
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    )
      return;
    e.preventDefault();
    navigate(to, { replace, state });
  };

  return (
    <a ref={ref} href={to} onClick={handleClick} target={target} {...rest}>
      {children}
    </a>
  );
});

export const NavLink = Link;

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();
  React.useEffect(() => {
    navigate(to, { replace });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);
  return null;
}

export function useLoaderData<T = unknown>(): T {
  return useRouterState({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    select: (s: any) => {
      const match = s.matches[s.matches.length - 1];
      return match?.loaderData;
    },
  } as never) as T;
}

export function ScrollRestoration() {
  return null;
}

export const Outlet = TanstackOutlet;
